import type { BrowserOptions } from "@sentry/browser";
import { hostEnvironment, isDevHost } from "./hostEnvironment";

// Sentry for the three.js editor, set up the way lureo-webxr sets up its viewer: off without a DSN and on a local or
// LAN host, never thrown out of, and with a release name like Unity's `lureo-unity@<version>+<build>`.

export interface SentryApi {
  init(options: BrowserOptions): unknown;
  setTag(key: string, value: string): void;
}

export interface ErrorReportingOptions {
  readonly dsn: string | null;
  readonly hostname: string;
  readonly version: string;
  readonly buildNumber: string;
  // import("@sentry/browser"): fetched only when reporting is on.
  readonly load: () => Promise<SentryApi>;
  readonly log: (message: string, detail?: unknown) => void;
}

export const sentryRelease = (version: string, buildNumber: string): string => `lureo-three@${version}+${buildNumber}`;

// Resolves true once Sentry is reporting.
export async function initErrorReporting(options: ErrorReportingOptions): Promise<boolean> {
  const { dsn, hostname } = options;
  if (dsn === null || isDevHost(hostname)) {
    options.log("[errors] Sentry disabled", { hasDsn: dsn !== null, devHost: isDevHost(hostname) });
    return false;
  }
  try {
    const sentry = await options.load();
    sentry.init({
      dsn,
      release: sentryRelease(options.version, options.buildNumber),
      environment: hostEnvironment(hostname),
      tracesSampleRate: 0,
    });
    sentry.setTag("client", "three");
    return true;
  } catch (error) {
    options.log("[errors] Sentry init failed", error);
    return false;
  }
}
